import type { MouseEvent } from 'react';
import { Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { hasCellDataForKpis, KpiCellStack } from './PackArchitectureTableCells';
import type { TableColumn, TableRow } from './packArchitectureTable.helpers';

type BuildTableColumnsParams = {
    visibleColumns: TableColumn[];
    orderedSelectedKpis: string[];
    yAxis: string;
    activeSegmentKey: string | null;
    selectedColumnKey: string | null;
    onCellClick: (segmentKey: string, columnKey: string) => void;
    onColumnHeaderClick: () => void;
    matchesKpiId: (cellKpiId: string, requestedKpiId: string) => boolean;
};

const getVendorCellClassName = (
    record: TableRow,
    colKey: string,
    orderedSelectedKpis: string[],
    activeSegmentKey: string | null,
    selectedColumnKey: string | null,
    matchesKpiId: (cellKpiId: string, requestedKpiId: string) => boolean,
) => {
    const classes = [
        hasCellDataForKpis(record.cells[colKey], orderedSelectedKpis, matchesKpiId)
            ? 'vendor-cell'
            : 'vendor-cell vendor-cell-empty',
    ];

    if (!activeSegmentKey) return classes.join(' ');

    if (record.key === activeSegmentKey) {
        classes.push('vendor-cell-row-selected');
        if (selectedColumnKey === colKey) {
            classes.push('vendor-cell-selected');
        }
    } else {
        classes.push('vendor-cell-dimmed');
    }

    return classes.join(' ');
};

export const buildPackArchitectureTableColumns = ({
    visibleColumns,
    orderedSelectedKpis,
    yAxis,
    activeSegmentKey,
    selectedColumnKey,
    onCellClick,
    onColumnHeaderClick,
    matchesKpiId,
}: BuildTableColumnsParams): ColumnsType<TableRow> => {
    const segmentColumn: ColumnsType<TableRow>[number] = {
        title: '',
        dataIndex: 'label',
        key: 'segment',
        fixed: 'left',
        width: 125,
        align: 'left',
        onHeaderCell: () => ({
            onClick: (event: MouseEvent) => {
                event.stopPropagation();
                onColumnHeaderClick();
            },
        }),
        onCell: (record: TableRow) => ({
            className:
                activeSegmentKey === record.key
                    ? 'segment-cell-selected'
                    : activeSegmentKey
                      ? 'segment-cell-dimmed'
                      : undefined,
        }),
        render: (label: string) => (
            <Tooltip title={label} placement="right">
                <span className="block max-w-[93px] truncate text-sm font-medium">{label}</span>
            </Tooltip>
        ),
    };

    const vendorColumns: ColumnsType<TableRow> = visibleColumns.map((col) => ({
        title: (
            <Tooltip title={col.label} placement="top">
                <span className="block max-w-[112px] truncate font-semibold">{col.label}</span>
            </Tooltip>
        ),
        key: col.key,
        align: 'left' as const,
        width: 144,
        onHeaderCell: () => ({
            onClick: (event: MouseEvent) => {
                event.stopPropagation();
                onColumnHeaderClick();
            },
        }),
        onCell: (record: TableRow) => ({
            className: getVendorCellClassName(
                record,
                col.key,
                orderedSelectedKpis,
                activeSegmentKey,
                selectedColumnKey,
                matchesKpiId,
            ),
            onClick: (event: MouseEvent) => {
                event.stopPropagation();
                onCellClick(record.key, col.key);
            },
        }),
        render: (_: unknown, record: TableRow) => (
            <KpiCellStack
                cell={record.cells[col.key]}
                selectedKpis={orderedSelectedKpis}
                yAxisKpiId={yAxis}
                matchesKpiId={matchesKpiId}
            />
        ),
    }));

    return [segmentColumn, ...vendorColumns];
};
